import { useMemo } from 'react';
import { css } from '@linaria/core';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { deviceBreakpoints } from '@cbhq/cds-web/layout/breakpoints';
import { FullscreenModal } from '@cbhq/cds-web/overlays/Modal/FullscreenModal';
import { Link, TextBody, TextDisplay3 } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import backpack from ':cloud/widgets/buildQuest/assets/backpack.png';
import bottle from ':cloud/widgets/buildQuest/assets/bottle.png';
import shirt from ':cloud/widgets/buildQuest/assets/shirt.png';
import sock from ':cloud/widgets/buildQuest/assets/sock.png';
import sticker from ':cloud/widgets/buildQuest/assets/sticker.png';
import tote from ':cloud/widgets/buildQuest/assets/tote.png';
import { useBuildQuestContext } from ':cloud/widgets/buildQuest/BuildQuestProvider';
import { ClaimPrizeModal } from ':cloud/widgets/buildQuest/ClaimPrizeModal';
import { Carousel, CarouselItem } from ':cloud/widgets/buildQuest/PrizesCarousel';
import { useOverrideFullscreenModalStyles } from ':cloud/widgets/buildQuest/useOverrideFullscreenModalStyles';

const defaultItems: CarouselItem[] = [
  {
    id: 1,
    name: 'CDP Sticker',
    imageUrl: sticker,
    isLocked: true,
    requiredLevel: 1,
  },
  {
    id: 2,
    name: 'CDP T-Shirt',
    imageUrl: shirt,
    isLocked: true,
    requiredLevel: 1,
  },
  {
    id: 3,
    name: 'CDP Tote',
    imageUrl: tote,
    isLocked: true,
    requiredLevel: 2,
  },
  {
    id: 4,
    name: 'CDP Socks',
    imageUrl: sock,
    isLocked: true,
    requiredLevel: 3,
  },
  {
    id: 5,
    name: 'CDP Bottle',
    imageUrl: bottle,
    isLocked: true,
    requiredLevel: 3,
  },
  {
    id: 6,
    name: 'CDP Backpack',
    imageUrl: backpack,
    isLocked: true,
    requiredLevel: 4,
  },
];

const modalContainer = css`
  padding: 0;
  margin: 0;
  overflow: hidden;
  .fsm-primary-content-container {
    padding: 0;
  }
`;

const topPositionTerms = css`
  position: fixed;
  top: 22px;
  left: 30px;
  left: 80px;
  z-index: 999;

  @media (min-width: ${deviceBreakpoints.phoneLandscape}px) {
    left: 30px;
  }
`;

export function BuildQuestPrizes(): React.ReactElement {
  const { isPrizesModalVisible } = useBuildQuestContext();
  useOverrideFullscreenModalStyles({ visible: isPrizesModalVisible });

  const { playerLevel, togglePrizesModalOff } = useBuildQuestContext();

  const { isPhone } = useSimpleBreakpoints();
  const [claimPrizeVisible, { toggleOn: claimPrizeModalOn, toggleOff: claimPrizeModalOff }] =
    useToggler(false);

  const items = useMemo(() => {
    return defaultItems.map((item) => ({
      ...item,
      isLocked: item.requiredLevel > playerLevel,
    }));
  }, [playerLevel]);

  const primaryContent = (
    <>
      <HStack className={topPositionTerms} gap={2}>
        <Link
          variant="body"
          color="foregroundMuted"
          openInNewWindow
          to={`${process.env.PUBLIC_URL}/pdf/Official-Rules-for-CDP-Quest-for-DevCon.pdf`}
          accessibilityLabel="terms"
        >
          Terms
        </Link>
        <Link
          variant="body"
          color="foregroundMuted"
          openInNewWindow
          to="https://www.coinbase.com/legal/privacy"
          accessibilityLabel="Privacy Policy"
        >
          Privacy Policy
        </Link>
        <Link
          variant="body"
          color="foregroundMuted"
          openInNewWindow
          to="https://www.coinbase.com/legal/cookie"
          accessibilityLabel="Cookie Policy"
        >
          Cookie Policy
        </Link>
      </HStack>
      <VStack width="100vw" height="100%" alignItems="center" position="relative" gap={8}>
        <VStack gap={2} alignItems="center" width="100%" spacing={2}>
          <HStack>
            <TextDisplay3 as="h3">CDP Quest Prizes</TextDisplay3>
          </HStack>

          <TextBody as="p" color="foregroundMuted" align="center">
            Complete challenges, get exp to level up, and unlock rewards each level.
            {isPhone ? ' ' : <br />}
            To claim your rewards, come see the CDP team at the Coinbase Developer Platform booth.
          </TextBody>
        </VStack>

        <Carousel items={items} claimPrizeModalOn={claimPrizeModalOn} />
      </VStack>
    </>
  );

  return (
    <>
      <FullscreenModal
        hideDivider
        visible={isPrizesModalVisible}
        dangerouslySetContentClassName={modalContainer}
        logo={<div />}
        onRequestClose={togglePrizesModalOff}
        primaryContent={primaryContent}
        closeAccessibilityLabel="Close"
        testID="custom-fullscreen-modal"
      />
      {claimPrizeVisible && <ClaimPrizeModal claimPrizeModalOff={claimPrizeModalOff} />}
    </>
  );
}
